import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
	makeStyles,
	useTheme,
	useMediaQuery,
	Typography,
	Divider,
	Box
} from '@material-ui/core';

import Fido from "../utils/Fido";
import Constants from "../utils/Constants";
import {useScrollTo} from "../utils/CustomHooks";
import {isJsonEqual} from "../utils/Json";
import AuthorizedUserContext from "../utils/AuthorizedUserContext";
import FilterSelect from "../components/FilterSelect";
import TrendsReportItem from "../components/TrendsReportItem";
import ActivityList from "../components/ActivityList";
import OverviewReportLayout from "../reports/components/OverviewReportLayout";
import RecentPostsItem from "../reports/components/RecentPostsItem";
import ReportItemPaper from "../reports/components/ReportItemPaper";
import TrendingTopicsReport from "../reports/containers/TrendingTopicsReport";
import {SmallerTabs, SmallerTab} from "../components/SmallerTabs";

//import {trendingTopicItemData} from "../data/reports.js";

const styles = makeStyles(theme => ({
	reportItem: {
		overflowY: "auto",

		"&::-webkit-scrollbar": {
			width: theme.spacing(1),
			backgroundColor: "#e9e9e9"
		},

		"&::-webkit-scrollbar-thumb": {
			borderRadius: 5,
			backgroundColor: "#c2c2c2"
		}
	},

	button: {
		bottom: 0,
		position: "relative",
		marginTop: theme.spacing(2)
	},

	scrollGrid: {
		backgroundColor: "#e9e9e9"
	},

	filterGridItem: {
		[theme.breakpoints.only('sm')]: {
			paddingTop: `${theme.spacing(4)}px !important`
		},

		[theme.breakpoints.down('sm')]: {
			paddingBottom: "0px !important"
		}
	},

	heading: {
		fontSize: 24,
		fontFamily: "Merriweather",
		fontWeight: 700,
		lineHeight: "32px", 
	}, 

	subHeading: {
		fontSize: 16,
	},

	tab: {
		fontWeight: 400
	},

	padding: {
		padding: "16px 40px",
	}
}));

const topicSources = {
	national: "national",
	ours: "ours"
};
const sourceOptions = [{
	id: topicSources.ours,
	name: "Our Topics"
},{
	id: topicSources.national,
	name: "National Topics"
}];

function TrendingTopics(props) {
	const theme = useTheme();
	const atLeastSM = useMediaQuery(theme.breakpoints.up("sm"));

	const authenticatedUser = useContext(AuthorizedUserContext);
	const defaultFilter = {
		previousDays: [30],
		account: authenticatedUser.childAccounts.map(item => item.id)
	};

	const [source, setSource] = useState(sourceOptions[0].id);
	const [filter, setFilter] = useState(defaultFilter);
	const [topicData, setTopicData] = useState(null);
	const [topicsFidoError, setTopicFidoError] = useState(false);
	const [discussionData, setDiscussionData] = useState(null);
	const [discussionFidoError, setDiscussionFidoError] = useState(false);
	const [readyForBubbles, setReadyForBubbles] = useState(false);

	const [topicToSelect, setTopicToSelect] = useState(null);
	const [selectedBucket, setSelectedBucket] = useState(null);
	const [discussions, setDiscussions] = useState(null);
	const [loading, setLoading] = useState(false);
	//const [bucketFidoError, setBucketFidoError] = useState(false);
	const [paging, setPaging] = useState({
		lastPage: 0,
		hasMore: false
	});

	const scrollToHook = useScrollTo();

	let classes = styles(),
		{previousDays} = filter,
		{updateHeader, resetHeader, location, history, showSnackbar} = props,
		pathname = location.pathname;

	useEffect(() => {
		updateHeader({title: "trending topics", primary: true});
		return () => resetHeader();
	}, [updateHeader, resetHeader]);

	// fetch data
	useEffect(() => {
		let topicsFido = new Fido(),
			activityFido = new Fido(),
			topicOpts = {query: Object.assign({sortBy: "posts"}, filter)},
			discussionOpts = {query: Object.assign({limit: 25, category: "discussions"}, filter)},
			topicsUrl = "/trending-topics";

		if (source === topicSources.national) {
			topicOpts.cors = true;
			delete topicOpts.query.sortBy;
			topicsUrl = Constants.SERVICES.NATIONAL_TRENDING_TOPICS.getUrl();
		}

		Promise.all([
			topicsFido.fetch(topicsUrl, topicOpts),
			activityFido.fetch("/activities", discussionOpts)
		])
			.then(data => {
				let topics = data[0],
					discussionResults = data[1];

				// TEMP ****************
				if (topics && topics.length) {
					if (source === topicSources.national) {
						topics.sort((a, b) => {
							return a.total > b.total ? -1 : a.total < b.total ? 1 : 0;
						});
					}
					setTopicData(topics);
				}
				//setTopicData(trendingTopicItemData);
				// TEMP ****************

				if (discussionResults) {
					setDiscussionData(discussionResults ? discussionResults.activities : []);
				}

				setReadyForBubbles(true);
			})
			.catch(error => {
				setTopicFidoError(true);
				setDiscussionFidoError(true);
				return showSnackbar(error.message);
			});

		return () => {
			topicsFido.dropIt();
			activityFido.dropIt();
		};
	}, [filter, showSnackbar, source]);

	// load list of discussion topics
	const loadTopics = (id) => {
		let telly = new Fido();
		setSelectedBucket(id);
		setDiscussions(null);

		if (!atLeastSM) {
			scrollToHook(0, 585); // topics card is a fixed height
		}

		(async function loadItems() {
			setLoading(true);
			// TEMP ******************
			let data = await telly.fetch(`/topics/${id}`, {query: {view: "removed"}})
			//let data = await telly.fetch(`/activities`, {query: {category: "discussions"}})
			// TEMP ******************
				.catch(error => {
					return showSnackbar(error.message);
				});

			if (data) {
				setDiscussions(data.activities);
				setPaging({
					lastPage: data.lastPage,
					hasMore: data.hasMoreItems
				});
				setLoading(false);
			}
		})();

		return () => telly.dropIt();
	};

	useEffect(() => {
		// if user clicked topic button from inbox
		let pieces = pathname.split("/"),
			id = pieces[3];

		if (id) {
			setTopicToSelect(Number(id));
		}
	}, [pathname, setTopicToSelect]);

	// paging
	const loadMore = (page) => {
		if (paging.hasMore && !loading) {
			let fido = new Fido();
			setLoading(true);

			(async function loadNextPage() {
				// TEMP ******************
				let data = await fido.fetch(`/topics/${selectedBucket}`, {query: {
					page: page,
					view: "removed",
					category: "discussions"
				}})
				/*let data = await fido.fetch("/activities", {query: {
					page: page,
					view: "removed",
					category: "discussions"
				}})*/
				// TEMP ******************
					.catch(error => {
						return showSnackbar(error.message);
					});

				if (data) {
					// append new items
					setDiscussions([...discussions, ...data.activities]);
					setPaging({
						lastPage: data.lastPage,
						hasMore: data.hasMoreItems
					});
					setLoading(false);
				}
			})();
		}
	};

	const executeFilter = (filterObj) => {
		if (!isJsonEqual(filter, filterObj)) {
			setReadyForBubbles(false);
			setTopicData(null);
			setDiscussionData(null);
			setFilter(filterObj);
		}
	};

	const filterSource = (selected) => {
		let s = selected;

		if (Object.keys(s).length && typeof s !== "string") {
			s = s.id;
		}

		if (!isJsonEqual(source,s)) {
			setReadyForBubbles(false);
			if (pathname !== "/trends/topics") {
				history.replace("/trends/topics");
			}
			setTopicData(null);
			setDiscussions(null);
			setDiscussionData(null);
			setSelectedBucket(null);
			setSource(s);
		}
	};

	let national = source === topicSources.national;

	const topicsOrRecents = topicData && topicData.length ?
		<TrendsReportItem
			id="trendingtopicspaper"
			className={classes.reportItem}
			buttonClassName={classes.button}
			fidoError={topicsFidoError}
			previousDays={previousDays[0]}
			topics={topicData}
			national={national}
			topicToSelect={topicToSelect}
			topicClickHandler={national ? null : (topic) => loadTopics(topic.id)}
			viewAllClickHandler={() => history.push("/inbox")} />
		:
		<RecentPostsItem
			taller
			className={classes.reportItem}
			activities={discussionData}
			history={history}
			fidoError={discussionFidoError}
			title={
				<React.Fragment>
					<FontAwesomeIcon title="conversation bubbles" icon={["far", ""]} /> Most Recent Discussions
				</React.Fragment>
			}
			itemClickPath="/inbox"
			buttonText="Go To Inbox"
			buttonClassName={classes.button}
			buttonClickHandler={() => history.push("/inbox")}
			noDataMessage="There are no recent discussions for this time period." />;

	let filterTitle = sourceOptions.filter(opt => opt.id === source)[0].name,
		filterFields = [
			<FilterSelect
				singleSelect
				name="source"
				key="sourceFilter"
				options={sourceOptions}
				menuTitle={filterTitle}
				handleUpdate={filterSource}
				defaultSelections={[sourceOptions[0]]} />
		];

	return (
		<>
		<Box sx={{ paddingTop: "16px", paddingLeft: "40px" }}>
		<SmallerTabs
			classes={classes.padding}
			value={source}
			indicatorColor="primary"
			onChange={(e, newValue) => filterSource(newValue)}>
			<SmallerTab
				value={topicSources.ours}
				className={`${classes.tab}`}
				label="Our Topics" />
			<SmallerTab
				value={topicSources.national}
				className={`${classes.tab}`}
				label="National Topics" />
		</SmallerTabs>
		</Box>
		<OverviewReportLayout
			location={location}
			showPreviousDays={false}
			previousDays={previousDays[0]}
			filterCallback={executeFilter}
			extraFilterFields={[]}
			filterGridItemClassName={classes.filterGridItem}>
			
			{topicsOrRecents}
			{discussions || selectedBucket ?
				<ReportItemPaper
					taller
					ready={true}
					role="group"
					aria-disabled="false"
					title={
						<div>
							<FontAwesomeIcon title="conversation bubbles" icon={["far", "comments-alt"]} /> Discussions
						</div>
					}>
					<ActivityList
						fitToParent
						history={history}
						items={discussions}
						isLoading={loading}
						loadMore={loadMore}
						lastPage={paging.lastPage}
						hasMore={paging.hasMore}
						pulsateAlerts={false}
						scrollGridClassName={classes.scrollGrid}
						clickHandler={(idx, id) => history.push(`/inbox/${id}`)} />
				</ReportItemPaper>
				:
				<Typography>
					{national ?
						<><span className={classes.heading}>National Trending Topics highlight the safety and security related discussions occurring across the United States.</span><br /><span className={classes.subHeading}>Our Language Engine is able to identify general chatter about topics related to safety and security, which are delivered as Awareness when relevant to your community. Our Data Science Team is able to take an unbiased sample of Awareness content occurring on public social media across the country, and group this content based on similarity. These emerging National patterns are highlighted as National Trending Topics.</span></>
						:
						<><span className={classes.heading}>Trending Topics highlight the safety and security related discussions occurring in your community.</span><br /><span className={classes.subHeading}>Our Language Engine identifies both potentially harmful or threatening content, which are delivered as Signals, as well as general chatter about topics related to safety and security, which are delivered as Awareness. Our Data Science Team uses Hierarchical Clustering to group the Awareness content based on similarity, and these emerging patterns are highlighted as Trending Topics.{topicData && topicData.length ? <React.Fragment><br /><br />Click on a Trending Topic to analyze all Awareness posts within the chosen topic.</React.Fragment> : null}</span></>
					}
				</Typography>
			}
			<TrendingTopicsReport
				fullWidth
				national={national}
				previousDays={previousDays[0]}
				updateHeader={updateHeader}
				resetHeader={resetHeader}
				showSnackbar={showSnackbar}
				readyToLoad={readyForBubbles} />
		</OverviewReportLayout>
		</>
	);
}

TrendingTopics.propTypes = {
	updateHeader: PropTypes.func.isRequired,
	resetHeader: PropTypes.func.isRequired,
	showSnackbar: PropTypes.func.isRequired,
	location: PropTypes.object,
	history: PropTypes.object
};

export default TrendingTopics;